import { useCallback, useEffect, useId, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeftIcon, PencilIcon, PlusIcon, Trash2Icon, UploadIcon } from 'lucide-react'
import type { ArticleSummary, ContentEntry, ContentMedia, ContentMenu, ContentMenuItem, ContentPartField, ContentPreview, ContentRevision, ContentSettings, ContentSummary } from '@hedgehog/shared'
import { api } from '@/lib/api'
import { articleStatus, articlesApi, fmtDate } from '@/components/articles/api'
import { ImageDialog } from '@/components/articles/image-dialog'
import { FormError, LoadingState, errorMessage } from '@/components/app/states'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Spinner } from '@/components/ui/spinner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { MenuPanel } from './menu-panel'

// The admin screen, full screen: everything on the site as lists, on the same content API as the
// page editor. A click on a page opens the page editor at that page.

export const ADMIN_SECTIONS = ['pages', 'articles', 'media', 'menus', 'parts', 'settings', 'deleted'] as const
export type AdminSection = (typeof ADMIN_SECTIONS)[number]

const SECTION_LABEL: Record<AdminSection, string> = {
  pages: 'Pages',
  articles: 'Articles',
  media: 'Media library',
  menus: 'Menus',
  parts: 'Header and footer',
  settings: 'Site settings',
  deleted: 'Deleted items',
}

interface Confirm {
  title: string
  description: string
  label: string
  run: () => Promise<void>
}

/** Loads on mount and on reload(). A section is mounted while it is open, so its list is always fresh. */
function useLoad<T>(load: (signal: AbortSignal) => Promise<T>) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const ctl = new AbortController()
    load(ctl.signal)
      .then((d) => {
        setData(d)
        setError(null)
      })
      .catch((e) => !ctl.signal.aborted && setError(errorMessage(e)))
    return () => ctl.abort()
  }, [tick])
  return { data, error, setData, reload: useCallback(() => setTick((t) => t + 1), []) }
}

/** Runs one action at a time, keeps its error and asks first when the action has a confirm step. */
function useActions() {
  const [working, setWorking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<Confirm | null>(null)
  const act = async (run: () => Promise<void>) => {
    setWorking(true)
    setError(null)
    try {
      await run()
    } catch (e) {
      setError(errorMessage(e))
    }
    setWorking(false)
  }
  const dialog = (
    <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
          <AlertDialogDescription>{confirm?.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              const c = confirm
              setConfirm(null)
              if (c) void act(c.run)
            }}
          >
            {confirm?.label}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
  return { working, error, act, ask: setConfirm, dialog }
}

function SectionHead({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{children}</p>
      </div>
      {action}
    </div>
  )
}

const rowList = 'flex flex-col divide-y divide-border rounded-xl border border-border bg-background'

function PagesSection({ orderId, base }: { orderId: string; base: string }) {
  const id = useId()
  const { data, error, reload } = useLoad((signal) => api<{ items: ContentSummary[] }>(`${base}/pages`, { signal }))
  const { working, error: actionError, act, ask, dialog } = useActions()
  const [title, setTitle] = useState('')
  const [from, setFrom] = useState('index')
  const [versions, setVersions] = useState<{ slug: string; items: ContentRevision[] } | null>(null)
  const url = (slug: string) => `${base}/pages/${encodeURIComponent(slug)}`

  const create = (e: FormEvent) => {
    e.preventDefault()
    void act(async () => {
      await api<ContentSummary>(`${base}/pages`, { method: 'POST', json: { title: title.trim(), from: from === 'index' ? undefined : from } })
      setTitle('')
      reload()
    })
  }
  const showVersions = (slug: string) =>
    act(async () => {
      const r = await api<{ items: ContentRevision[] }>(`${url(slug)}/revisions`)
      setVersions({ slug, items: r.items })
    })
  const restore = (p: ContentSummary, v: ContentRevision) => {
    const run = async () => {
      await api(`${url(p.slug)}/restore/${encodeURIComponent(v.id)}`, { method: 'POST' })
      setVersions(null)
      reload()
    }
    if (p.hasDraft) ask({ title: 'Use this version?', description: 'This version replaces the unpublished changes on this page.', label: 'Use this version', run })
    else void act(run)
  }

  return (
    <section className="flex flex-col gap-4">
      <SectionHead title="Pages">A click on a page opens it in the editor. A hidden page stays out of the site until you publish it.</SectionHead>
      <FormError message={error ?? actionError} />
      {!data && !error && <LoadingState label="Loading pages" />}
      {data && (
        <ul className={rowList}>
          {data.items.map((p) => {
            const live = p.status === 'published'
            const home = p.slug === 'index'
            return (
              <li key={p.slug} className="flex flex-col gap-2 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Link to="/o/$orderId/edit" params={{ orderId }} search={{ page: p.slug }} className="min-w-0 flex-1 truncate font-medium underline-offset-4 hover:underline">
                    {p.title || p.slug}
                  </Link>
                  <Badge variant={live ? 'secondary' : 'outline'}>{live ? 'Live' : 'Hidden'}</Badge>
                  {p.hasDraft && <Badge variant="outline">Unpublished changes</Badge>}
                  <Button size="sm" variant="outline" disabled={working} onClick={() => void showVersions(p.slug)}>
                    Versions
                  </Button>
                  {(!live || p.hasDraft) && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={working}
                      onClick={() =>
                        void act(async () => {
                          await api(`${url(p.slug)}/publish`, { method: 'POST' })
                          reload()
                        })
                      }
                    >
                      {live ? 'Publish changes' : 'Publish'}
                    </Button>
                  )}
                  {live && !home && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={working}
                      onClick={() =>
                        ask({
                          title: 'Hide this page?',
                          description: 'Visitors stop seeing this page at once. Links to it stop working. You can put it back at any time.',
                          label: 'Hide page',
                          run: async () => {
                            await api(`${url(p.slug)}/unpublish`, { method: 'POST' })
                            reload()
                          },
                        })
                      }
                    >
                      Hide
                    </Button>
                  )}
                  {!home && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-destructive"
                      disabled={working}
                      onClick={() =>
                        ask({
                          title: 'Delete this page?',
                          description: 'Visitors stop seeing this page at once. You can bring it back from Deleted items.',
                          label: 'Delete page',
                          run: async () => {
                            await api(url(p.slug), { method: 'DELETE' })
                            reload()
                          },
                        })
                      }
                    >
                      Delete
                    </Button>
                  )}
                </div>
                {versions?.slug === p.slug &&
                  (versions.items.length === 0 ? (
                    <p className="text-sm text-muted-foreground">This page has no earlier versions yet. Each publish keeps one.</p>
                  ) : (
                    <ul className="flex flex-col gap-1 text-sm">
                      {versions.items.map((v) => (
                        <li key={v.id} className="flex items-center justify-between gap-2">
                          <time dateTime={v.createdAt}>
                            {new Date(v.createdAt).toLocaleString()} · {v.actor}
                          </time>
                          <Button size="sm" variant="ghost" disabled={working} onClick={() => restore(p, v)}>
                            Use this version
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ))}
              </li>
            )
          })}
        </ul>
      )}
      {data && (
        <form onSubmit={create} className="flex flex-col gap-2 rounded-xl border border-border bg-background p-3 sm:flex-row sm:items-end">
          <div className="flex flex-1 flex-col gap-2">
            <Label htmlFor={`${id}-t`}>New page</Label>
            <Input id={`${id}-t`} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="Page title" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor={`${id}-f`}>Same look as</Label>
            <NativeSelect id={`${id}-f`} value={from} onChange={(e) => setFrom(e.target.value)}>
              {data.items.map((p) => (
                <NativeSelectOption key={p.slug} value={p.slug}>
                  {p.title || p.slug}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </div>
          <Button type="submit" disabled={working || !title.trim()}>
            <PlusIcon aria-hidden />
            Create
          </Button>
        </form>
      )}
      {dialog}
    </section>
  )
}

function ArticlesSection({ orderId, blog }: { orderId: string; blog: boolean }) {
  const { data, error } = useLoad((signal) => (blog ? articlesApi(orderId).list(signal) : Promise.resolve({ items: [] as ArticleSummary[] })))
  if (!blog) return <SectionHead title="Articles">This site has no blog.</SectionHead>
  return (
    <section className="flex flex-col gap-4">
      <SectionHead
        title="Articles"
        action={
          <Link to="/o/$orderId/articles/$postId" params={{ orderId, postId: 'new' }} search={{ from: 'admin' }} className={buttonVariants()}>
            <PlusIcon aria-hidden />
            New article
          </Link>
        }
      >
        Drafts stay private until you publish them.
      </SectionHead>
      <FormError message={error} />
      {!data && !error && <LoadingState label="Loading articles" />}
      {data && data.items.length === 0 && <p className="text-sm text-muted-foreground">No articles yet.</p>}
      {data && data.items.length > 0 && (
        <ul className={rowList}>
          {data.items.map((a) => (
            <li key={a.id}>
              <Link to="/o/$orderId/articles/$postId" params={{ orderId, postId: a.id }} search={{ from: 'admin' }} className="flex flex-wrap items-center gap-2 p-3 hover:bg-muted/50">
                <span className="min-w-0 flex-1 truncate font-medium">{a.title || 'Untitled'}</span>
                {a.updatedAt && <span className="text-sm text-muted-foreground">Edited {fmtDate(a.updatedAt)}</span>}
                <Badge variant="outline">{articleStatus(a).label}</Badge>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function MediaCard({ media, origin, base, working, act, ask, onChanged }: { media: ContentMedia; origin: string; base: string; working: boolean; act: (run: () => Promise<void>) => Promise<void>; ask: (c: Confirm) => void; onChanged: () => void }) {
  const id = useId()
  const [alt, setAlt] = useState(media.value.alt)
  const url = `${base}/media/${encodeURIComponent(media.value.id)}`
  return (
    <li className="flex flex-col gap-2 rounded-xl border border-border bg-background p-3">
      <div className="flex aspect-video items-center justify-center overflow-hidden rounded-lg bg-muted/40">
        <img src={origin + media.url} alt="" loading="lazy" className="max-h-full max-w-full object-contain" />
      </div>
      <p className="truncate text-sm text-muted-foreground">{media.value.filename ?? media.value.id}</p>
      <Label htmlFor={id}>Description (alt text)</Label>
      <Input id={id} value={alt} onChange={(e) => setAlt(e.target.value)} maxLength={1000} />
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={working || alt === media.value.alt}
          onClick={() =>
            void act(async () => {
              await api<ContentMedia>(url, { method: 'PUT', json: { alt: alt.trim() } })
              onChanged()
            })
          }
        >
          Save
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="text-destructive"
          disabled={working}
          onClick={() =>
            ask({
              title: 'Delete this image?',
              description: 'The image leaves the media library and the site. This cannot be undone.',
              label: 'Delete image',
              run: async () => {
                await api<{ deleted: true }>(url, { method: 'DELETE' })
                onChanged()
              },
            })
          }
        >
          <Trash2Icon aria-hidden />
          Delete
        </Button>
      </div>
    </li>
  )
}

function MediaSection({ base }: { base: string }) {
  const { data, error, reload } = useLoad(async (signal) => {
    const [media, preview] = await Promise.all([api<{ items: ContentMedia[] }>(`${base}/media`, { signal }), api<ContentPreview>(`${base}/preview?path=/`, { signal })])
    return { items: media.items, origin: preview.base }
  })
  const { working, error: actionError, act, ask, dialog } = useActions()
  const [uploading, setUploading] = useState(false)
  const upload = async (file: File | null, alt: string): Promise<string | null> => {
    if (!file) return 'Choose an image first.'
    try {
      await api<ContentMedia>(`${base}/media`, {
        method: 'POST',
        body: file,
        headers: { 'content-type': file.type || 'application/octet-stream', 'x-hh-filename': encodeURIComponent(file.name), 'x-hh-alt': encodeURIComponent(alt) },
      })
      reload()
      return null
    } catch (e) {
      return errorMessage(e)
    }
  }
  return (
    <section className="flex flex-col gap-4">
      <SectionHead
        title="Media library"
        action={
          <Button onClick={() => setUploading(true)}>
            <UploadIcon aria-hidden />
            Upload image
          </Button>
        }
      >
        The images you can use on the site. A page that uses an image keeps its own description.
      </SectionHead>
      <FormError message={error ?? actionError} />
      {!data && !error && <LoadingState label="Loading images" />}
      {data && data.items.length === 0 && <p className="text-sm text-muted-foreground">No images yet.</p>}
      {data && data.items.length > 0 && (
        <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {data.items.map((m) => (
            <MediaCard key={`${m.value.id}:${m.value.alt}`} media={m} origin={data.origin} base={base} working={working} act={act} ask={ask} onChanged={reload} />
          ))}
        </ul>
      )}
      <ImageDialog open={uploading} title="Upload image" description="The image goes to the media library. It does not change any page." withCaption={false} onClose={() => setUploading(false)} onSubmit={upload} />
      {dialog}
    </section>
  )
}

function MenusSection({ base }: { base: string }) {
  const { data, error, setData, reload } = useLoad(async (signal) => {
    const [menu, pages] = await Promise.all([api<ContentMenu>(`${base}/menus/primary`, { signal }), api<{ items: ContentSummary[] }>(`${base}/pages`, { signal })])
    return { menu, pages: pages.items }
  })
  const { working, error: actionError, act } = useActions()
  const save = async (items: ContentMenuItem[]): Promise<string | null> => {
    if (!data) return 'The menu is not loaded.'
    try {
      const r = await api<{ base: string }>(`${base}/menus/primary`, { method: 'PUT', json: { items, base: data.menu.base } })
      setData({ ...data, menu: { ...data.menu, items, hasDraft: true, base: r.base } })
      return null
    } catch (e) {
      return errorMessage(e)
    }
  }
  return (
    <section className="flex max-w-xl flex-col gap-4">
      <SectionHead
        title="Menus"
        action={
          data?.menu.hasDraft ? (
            <Button
              disabled={working}
              onClick={() =>
                void act(async () => {
                  await api(`${base}/menus/primary/publish`, { method: 'POST' })
                  reload()
                })
              }
            >
              {working && <Spinner />}
              Publish menu
            </Button>
          ) : undefined
        }
      >
        The main menu of the site. Changes stay a draft until you publish them.
      </SectionHead>
      <FormError message={error ?? actionError} />
      {!data && !error && <LoadingState label="Loading the menu" />}
      {data && <MenuPanel menu={data.menu} pages={data.pages} onSave={save} />}
    </section>
  )
}

function PartForm({ base, entry, working, act, ask, onChanged }: { base: string; entry: ContentEntry; working: boolean; act: (run: () => Promise<void>) => Promise<void>; ask: (c: Confirm) => void; onChanged: () => void }) {
  const id = useId()
  const fields = (entry.data.fields ?? []) as ContentPartField[]
  const [values, setValues] = useState(() => Object.fromEntries(fields.map((f) => [f.ref, { text: f.text, url: f.url }])))
  const url = `${base}/parts/${encodeURIComponent(entry.slug)}`
  const changed = fields.filter((f) => values[f.ref]?.text !== f.text || values[f.ref]?.url !== f.url)
  const set = (ref: string, patch: { text?: string; url?: string }) => setValues((v) => ({ ...v, [ref]: { ...v[ref], ...patch } }))
  const name = entry.slug.charAt(0).toUpperCase() + entry.slug.slice(1)

  const save = (e: FormEvent) => {
    e.preventDefault()
    void act(async () => {
      await api(url, { method: 'PUT', json: { rev: entry.rev, edits: changed.map((f) => ({ ref: f.ref, ...values[f.ref] })) } })
      onChanged()
    })
  }

  return (
    <form onSubmit={save} className="flex flex-col gap-3 rounded-xl border border-border bg-background p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="flex-1 font-medium">{name}</h3>
        {entry.hasDraft && <Badge variant="outline">Unpublished changes</Badge>}
      </div>
      {fields.length === 0 && <p className="text-sm text-muted-foreground">This part has no text or links to change here.</p>}
      {fields.map((f, i) => {
        const v = values[f.ref]
        const field = `${id}-${i}`
        if (f.kind === 'link') {
          return (
            <fieldset key={f.ref} className="grid gap-2 sm:grid-cols-2">
              <legend className="sr-only">Link {i + 1}</legend>
              {f.text !== undefined && (
                <div className="flex flex-col gap-2">
                  <Label htmlFor={`${field}-t`}>Link text</Label>
                  <Input id={`${field}-t`} value={v?.text ?? ''} onChange={(e) => set(f.ref, { text: e.target.value })} />
                </div>
              )}
              <div className="flex flex-col gap-2">
                <Label htmlFor={`${field}-u`}>Link address</Label>
                <Input id={`${field}-u`} value={v?.url ?? ''} onChange={(e) => set(f.ref, { url: e.target.value })} />
              </div>
            </fieldset>
          )
        }
        return (
          <div key={f.ref} className="flex flex-col gap-2">
            <Label htmlFor={field}>Text</Label>
            {f.kind === 'rich' ? <Textarea id={field} value={v?.text ?? ''} onChange={(e) => set(f.ref, { text: e.target.value })} /> : <Input id={field} value={v?.text ?? ''} onChange={(e) => set(f.ref, { text: e.target.value })} />}
          </div>
        )
      })}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" variant="outline" disabled={working || changed.length === 0}>
          Save draft
        </Button>
        {entry.hasDraft && (
          <>
            <Button
              type="button"
              size="sm"
              disabled={working || changed.length > 0}
              onClick={() =>
                void act(async () => {
                  await api(`${url}/publish`, { method: 'POST' })
                  onChanged()
                })
              }
            >
              Publish
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={working}
              onClick={() =>
                ask({
                  title: 'Discard the changes?',
                  description: 'This part goes back to what visitors see now. The unpublished changes are lost.',
                  label: 'Discard changes',
                  run: async () => {
                    await api(`${url}/discard`, { method: 'POST' })
                    onChanged()
                  },
                })
              }
            >
              Discard changes
            </Button>
          </>
        )}
      </div>
    </form>
  )
}

function PartsSection({ orderId, base }: { orderId: string; base: string }) {
  const { data, error, reload } = useLoad(async (signal) => {
    const parts = await api<{ items: ContentSummary[] }>(`${base}/parts`, { signal })
    return Promise.all(parts.items.map((p) => api<ContentEntry>(`${base}/parts/${encodeURIComponent(p.slug)}`, { signal })))
  })
  const { working, error: actionError, act, ask, dialog } = useActions()
  return (
    <section className="flex max-w-2xl flex-col gap-4">
      <SectionHead title="Header and footer">
        The parts every page shows. To change an image, click it on a page in{' '}
        <Link to="/o/$orderId/edit" params={{ orderId }} className="underline underline-offset-4">
          the editor
        </Link>
        .
      </SectionHead>
      <FormError message={error ?? actionError} />
      {!data && !error && <LoadingState label="Loading the header and footer" />}
      {/* The key holds the rev: a saved or published part starts again from what the API returned. */}
      {data?.map((e) => <PartForm key={`${e.slug}:${e.rev}:${e.hasDraft}`} base={base} entry={e} working={working} act={act} ask={ask} onChanged={reload} />)}
      {dialog}
    </section>
  )
}

function SettingsSection({ base }: { base: string }) {
  const id = useId()
  const { data, error, setData } = useLoad((signal) => api<ContentSettings>(`${base}/settings`, { signal }))
  const { working, error: actionError, act } = useActions()
  const [note, setNote] = useState<string | null>(null)
  const save = (e: FormEvent) => {
    e.preventDefault()
    if (!data) return
    setNote(null)
    void act(async () => {
      setData(await api<ContentSettings>(`${base}/settings`, { method: 'PUT', json: data }))
      setNote('Saved and published.')
    })
  }
  return (
    <section className="flex max-w-xl flex-col gap-4">
      <SectionHead title="Site settings">Search engines and AI tools read these. Each page keeps its own title. A save goes live at once.</SectionHead>
      <FormError message={error ?? actionError} />
      {!data && !error && <LoadingState label="Loading settings" />}
      {data && (
        <form onSubmit={save} className="flex flex-col gap-2 rounded-xl border border-border bg-background p-4">
          <Label htmlFor={`${id}-n`}>Site title</Label>
          <Input id={`${id}-n`} value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} maxLength={200} required />
          <Label htmlFor={`${id}-t`}>Tagline</Label>
          <Input id={`${id}-t`} value={data.tagline} onChange={(e) => setData({ ...data, tagline: e.target.value })} maxLength={500} />
          <div className="flex items-center gap-2">
            <Button type="submit" size="sm" variant="outline" disabled={working || !data.title.trim()}>
              {working && <Spinner />}
              Save
            </Button>
            {note && (
              <span role="status" className="text-sm text-muted-foreground">
                {note}
              </span>
            )}
          </div>
        </form>
      )}
    </section>
  )
}

function DeletedSection({ orderId, base }: { orderId: string; base: string }) {
  const { data, error, reload } = useLoad((signal) => api<{ items: ContentSummary[]; posts?: ArticleSummary[] }>(`${base}/trash`, { signal }))
  const { working, error: actionError, act } = useActions()
  const rows = [
    ...(data?.items ?? []).map((p) => ({ key: `page:${p.slug}`, title: p.title || p.slug, kind: 'Page', back: () => api(`${base}/pages/${encodeURIComponent(p.slug)}/undelete`, { method: 'POST' }) })),
    ...(data?.posts ?? []).map((a) => ({ key: `post:${a.id}`, title: a.title || 'Untitled', kind: 'Article', back: () => articlesApi(orderId).undelete(a.id) })),
  ]
  return (
    <section className="flex flex-col gap-4">
      <SectionHead title="Deleted items">An item comes back as a draft. Publish it to show it again.</SectionHead>
      <FormError message={error ?? actionError} />
      {!data && !error && <LoadingState label="Loading deleted items" />}
      {data && rows.length === 0 && <p className="text-sm text-muted-foreground">Nothing is deleted.</p>}
      {rows.length > 0 && (
        <ul className={rowList}>
          {rows.map((r) => (
            <li key={r.key} className="flex items-center gap-2 p-3">
              <span className="min-w-0 flex-1 truncate">{r.title}</span>
              <Badge variant="outline">{r.kind}</Badge>
              <Button
                size="sm"
                variant="outline"
                disabled={working}
                onClick={() =>
                  void act(async () => {
                    await r.back()
                    reload()
                  })
                }
              >
                Bring back
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export function AdminScreen({ orderId, businessName, blog, section }: { orderId: string; businessName: string; blog: boolean; section: AdminSection }) {
  const base = `/orders/${encodeURIComponent(orderId)}/content`
  const navigate = useNavigate()
  return (
    <div className="flex h-dvh flex-col bg-muted/40">
      <header className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border bg-background px-2 py-2 sm:px-3">
        <Link to="/o/$orderId/editor" params={{ orderId }} className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2 text-sm font-medium outline-none hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50">
          <ArrowLeftIcon className="size-4" aria-hidden />
          <span className="max-w-40 truncate">{businessName}</span>
          <span className="sr-only">: back to your account</span>
        </Link>
        <span className="hidden h-5 w-px bg-border sm:block" aria-hidden />
        <h1 className="flex-1 text-sm font-medium">Admin</h1>
        <Link to="/o/$orderId/edit" params={{ orderId }} className={buttonVariants({ size: 'sm' })}>
          <PencilIcon aria-hidden />
          Open the editor
        </Link>
      </header>
      <Tabs
        orientation="vertical"
        value={section}
        onValueChange={(v) => void navigate({ to: '.', search: (s: Record<string, unknown>) => ({ ...s, section: v as AdminSection }), replace: true })}
        className="min-h-0 flex-1 flex-col gap-0 md:flex-row"
      >
        <TabsList variant="line" className="w-full shrink-0 flex-row justify-start overflow-x-auto rounded-none border-b border-border bg-background p-2 md:w-52 md:flex-col md:items-stretch md:border-r md:border-b-0">
          {ADMIN_SECTIONS.map((s) => (
            <TabsTrigger key={s} value={s} className="flex-none justify-start md:h-8 md:w-full">
              {SECTION_LABEL[s]}
            </TabsTrigger>
          ))}
        </TabsList>
        <div id="main" className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          <TabsContent value="pages">
            <PagesSection orderId={orderId} base={base} />
          </TabsContent>
          <TabsContent value="articles">
            <ArticlesSection orderId={orderId} blog={blog} />
          </TabsContent>
          <TabsContent value="media">
            <MediaSection base={base} />
          </TabsContent>
          <TabsContent value="menus">
            <MenusSection base={base} />
          </TabsContent>
          <TabsContent value="parts">
            <PartsSection orderId={orderId} base={base} />
          </TabsContent>
          <TabsContent value="settings">
            <SettingsSection base={base} />
          </TabsContent>
          <TabsContent value="deleted">
            <DeletedSection orderId={orderId} base={base} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  )
}
